// Per-user "seen it" flags (2026-08-07), stored on the ClaudeUsers profile
// row via claude-user-api. Once-only UI — the student onboarding, the teacher
// welcome guide, and the page tours — must key off the USER, not the browser:
// students share Chromebooks, and localStorage dies with incognito or a
// cleared profile. The server map is the authority; localStorage stays as a
// fast cache and offline fallback, so nothing regresses when the network is
// down (worst case: the old per-browser behavior).

// Stage-aware endpoint (src/config/api.js): npm start -> Development, builds -> Production.
import { USER_API_URL } from '../../config/api';

let cache = null; // { flagKey: iso } once loaded; null = not loaded / failed
let inflight = null;
let cachedFor = null; // which user the cache belongs to

async function call(auth, action, params = {}) {
  const token = auth?.user?.idToken || auth?.user?.accessToken;
  if (!token) throw new Error('not signed in');
  const res = await fetch(USER_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ action, ...params }),
  });
  if (!res.ok) throw new Error(`user api ${action} failed (${res.status})`);
  return res.json();
}

// One fetch per user per page load; every caller shares the same promise.
// Resolves to the flags map, or null when signed out / the fetch failed —
// callers then fall back to their localStorage check alone.
export function loadUiFlags(auth) {
  const uid = auth?.user?.username;
  if (!uid) return Promise.resolve(null);
  if (inflight && cachedFor === uid) return inflight;
  cachedFor = uid;
  inflight = call(auth, 'getUiFlags')
    .then((r) => { cache = r.flags || {}; return cache; })
    .catch(() => { cache = null; return null; });
  return inflight;
}

export const hasUiFlag = (key) => !!(cache && cache[key]);

// Fire-and-forget: the localStorage mirror already blocks a same-browser
// repeat, so a lost write only costs cross-device dedupe for that one flag.
export function setUiFlag(auth, key) {
  if (cache) cache[key] = new Date().toISOString();
  if (!auth?.user) return;
  call(auth, 'setUiFlag', { key }).catch(() => { /* mirror covers it */ });
}
